import { useState, useEffect, useCallback } from 'react'
import { useAccount } from 'wagmi'
import { Icon } from './Layout'
import { getActiveCampaigns, getMyClaims, claimCampaign } from '../services/bounty'
import { ReferralDashboard } from './ReferralDashboard'
import { humanizeApiError } from '../services/client'

const TYPE_META = {
  twitter_repost: { label: 'Repost', icon: 'repeat' },
  referral: { label: 'Referral', icon: 'person_add' },
  custom: { label: 'Task', icon: 'task_alt' },
}

function estimateCptReward(campaign) {
  if (campaign.rewardMode === 'cpt_fixed') return campaign.rewardAmountCpt ?? 0
  const usd = Number(campaign.rewardValueUsd ?? 0)
  const price = Number(campaign.cptPriceUsd ?? 0)
  if (!price) return 0
  return Math.round(usd / price)
}

function StateBadge({ state }) {
  const meta = {
    eligible: { label: 'Eligible', cls: 'border-on-background/30 text-on-surface-variant' },
    claimed: { label: 'Claimed', cls: 'border-primary-container bg-primary-container/15 text-on-primary-container' },
    rejected: { label: 'Rejected', cls: 'border-error text-error' },
    blocked: { label: 'Blocked', cls: 'border-error text-error' },
  }[state]
  if (!meta) return null
  return (
    <span className={`px-2 py-0.5 border-2 font-bold text-[10px] uppercase tracking-widest ${meta.cls}`}>
      {meta.label}
    </span>
  )
}

function ClaimModal({ campaign, walletAddress, onClose, onClaimed }) {
  const [wallet, setWallet] = useState(walletAddress ?? '')
  const [proofUrl, setProofUrl] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Referral campaigns never reach this modal: the backend pays them
  // automatically and rejects POST /claim on them (see ReferralDashboard).
  const submit = async () => {
    if (!wallet.trim()) { setError('A wallet address is required to claim CPT'); return }
    if (!proofUrl.trim()) { setError('Paste a link to your post as proof'); return }

    setLoading(true)
    setError(null)
    try {
      await claimCampaign(campaign.id, {
        walletAddress: wallet.trim(),
        proofUrl: proofUrl.trim(),
      })
      onClaimed()
    } catch (err) {
      setError(humanizeApiError(err))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-on-background/60 p-3 sm:p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="bg-surface border-4 border-on-background w-full max-w-sm flex flex-col gap-4 p-5 sm:p-6 max-h-[90vh] overflow-y-auto"
        style={{ boxShadow: '6px 6px 0px 0px var(--neo-border-color)' }}>
        <div className="flex items-center justify-between">
          <h2 className="font-extrabold text-[14px] uppercase tracking-widest">Claim Bounty</h2>
          <button onClick={onClose}>
            <span className="material-symbols-outlined text-[20px]">close</span>
          </button>
        </div>

        <div>
          <p className="font-bold text-[13px]">{campaign.title}</p>
          <p className="text-[11px] text-on-surface-variant mt-1">{campaign.description}</p>
          {campaign.targetUrl && (
            <a href={campaign.targetUrl} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[11px] font-bold text-primary-container mt-2 hover:underline">
              <Icon name="open_in_new" className="text-[13px]" />
              Open target post
            </a>
          )}
        </div>

        {error && (
          <div className="border-2 border-error bg-error-container/40 text-on-error-container p-2 text-[11px] font-bold">
            {error}
          </div>
        )}

        <div className="flex flex-col gap-1">
          <label className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">
            Wallet address
          </label>
          <input
            type="text"
            value={wallet}
            onChange={e => setWallet(e.target.value)}
            placeholder="0x..."
            className="w-full bg-surface border-2 border-on-background px-3 py-2 text-[13px] font-mono focus:outline-none focus:border-primary-container"
          />
          <p className="text-[10px] text-on-surface-variant/70 mt-0.5">
            This wallet can only be used on one account — using the same wallet or IP across
            multiple accounts blocks both from bounty rewards.
          </p>
        </div>

        <div className="flex flex-col gap-1">
          <label className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">
            Link to your post
          </label>
          <input
            type="url"
            value={proofUrl}
            onChange={e => setProofUrl(e.target.value)}
            placeholder="https://x.com/you/status/..."
            className="w-full bg-surface border-2 border-on-background px-3 py-2 text-[13px] focus:outline-none focus:border-primary-container"
          />
        </div>

        <div className="flex gap-2 mt-1 sticky bottom-0 bg-surface pt-1">
          <button onClick={onClose} disabled={loading}
            className="flex-1 px-4 py-2.5 border-2 border-on-background font-bold text-[11px] uppercase tracking-widest hover:bg-surface-container transition-colors disabled:opacity-50">
            Cancel
          </button>
          <button onClick={submit} disabled={loading}
            className="flex-1 px-4 py-2.5 border-2 border-primary-container bg-primary-container text-on-primary-container font-bold text-[11px] uppercase tracking-widest hover:brightness-105 transition-colors disabled:opacity-50">
            {loading ? 'Claiming…' : 'Claim'}
          </button>
        </div>
      </div>
    </div>
  )
}

function CampaignCard({ campaign, onClaim }) {
  const meta = TYPE_META[campaign.taskType] ?? TYPE_META.custom
  const canClaim = !campaign.myClaimStatus
  const reward = estimateCptReward(campaign)

  return (
    <div className="border-2 border-on-background/20 bg-surface-container p-3 sm:p-4 flex flex-col gap-2 min-w-0">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-8 h-8 flex items-center justify-center border-2 border-on-background/20 shrink-0">
            <Icon name={meta.icon} className="text-[16px]" />
          </span>
          <div className="min-w-0">
            <p className="font-extrabold text-[13px] leading-tight break-words">{campaign.title}</p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">{meta.label}</p>
          </div>
        </div>
        <span className="font-bold text-[12px] text-primary-container shrink-0">
          {campaign.rewardMode === 'usd_value' ? '≈' : '+'}{reward.toLocaleString()} CPT
        </span>
      </div>

      <p className="text-[11px] text-on-surface-variant leading-snug break-words">{campaign.description}</p>

      <div className="flex items-center justify-between gap-2 mt-1 flex-wrap">
        <StateBadge state={campaign.myClaimStatus} />
        {canClaim && (
          <button
            onClick={() => onClaim(campaign)}
            className="px-3 py-1.5 border-2 border-on-background font-bold text-[10px] uppercase tracking-widest hover:bg-on-background hover:text-surface transition-colors active:scale-95"
          >
            Claim
          </button>
        )}
      </div>

      {campaign.poolRemainingCpt != null && (
        <p className="text-[10px] text-on-surface-variant/70 font-mono">
          {Math.max(0, campaign.poolRemainingCpt).toLocaleString()} CPT left in campaign pool
        </p>
      )}
    </div>
  )
}

function MyClaimsHistory({ claims }) {
  const [open, setOpen] = useState(false)
  if (claims.length === 0) return null

  return (
    <div className="border-2 border-on-background/20 bg-surface-container-low">
      <button onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3">
        <span className="font-bold text-[11px] uppercase tracking-widest">
          My Bounty Claims ({claims.length})
        </span>
        <Icon name={open ? 'expand_less' : 'expand_more'} className="text-[18px]" />
      </button>
      {open && (
        <div className="flex flex-col gap-2 px-3 sm:px-4 pb-4">
          {claims.map(c => (
            <div key={c.id} className="flex items-center justify-between gap-2 border-t border-on-background/10 pt-2 flex-wrap">
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-mono truncate opacity-70">
                  {c.proofUrl ?? (c.walletAddress ? `${c.walletAddress.slice(0, 10)}…` : 'Referral claim')}
                </p>
                {c.rejectionReason && (
                  <p className="text-[10px] text-error mt-0.5 break-words">{c.rejectionReason}</p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="font-bold text-[11px] text-primary-container">+{c.cptAmount} CPT</span>
                <StateBadge state={c.status} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export function BountyPanel() {
  const { address } = useAccount()
  const [campaigns, setCampaigns] = useState([])
  const [claims, setClaims] = useState([])
  const [loading, setLoading] = useState(true)
  const [modalCampaign, setModalCampaign] = useState(null)
  const [successMsg, setSuccessMsg] = useState(null)

  const load = useCallback(() => {
    setLoading(true)
    Promise.all([getActiveCampaigns(), getMyClaims()])
      .then(([c, cl]) => { setCampaigns(c); setClaims(cl) })
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  // Referral campaigns get the auto-paid dashboard (no claim, no pool);
  // everything else keeps the claim-card flow.
  const referralCampaigns = campaigns.filter(c => c.taskType === 'referral')
  const claimCampaigns = campaigns.filter(c => c.taskType !== 'referral')

  const handleClaimed = () => {
    setModalCampaign(null)
    setSuccessMsg('Bounty claimed — CPT has been credited to your balance.')
    setTimeout(() => setSuccessMsg(null), 4000)
    load()
  }

  return (
    <div className="flex flex-col gap-4">
      {modalCampaign && (
        <ClaimModal
          campaign={modalCampaign}
          walletAddress={address}
          onClose={() => setModalCampaign(null)}
          onClaimed={handleClaimed}
        />
      )}

      <div className="bg-on-background text-inverse-on-surface p-3 sm:p-4 flex items-center gap-3">
        <Icon name="military_tech" className="text-[20px] opacity-80 shrink-0" />
        <p className="flex-1 text-[11px] leading-snug opacity-90">
          Time-limited campaigns — repost or complete a featured task for a share of a fixed CPT pool,
          or refer friends and earn CPT automatically as each one qualifies.
        </p>
      </div>

      {successMsg && (
        <div className="border-2 border-primary-container bg-primary-container/15 text-on-primary-container p-3 text-[11px] font-bold">
          {successMsg}
        </div>
      )}

      {loading ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-24 bg-surface-container animate-pulse" />
          ))}
        </div>
      ) : campaigns.length === 0 ? (
        <div className="border-2 border-dashed border-on-background/20 p-6 sm:p-8 text-center">
          <p className="font-bold text-[12px] uppercase tracking-widest text-on-surface-variant">
            No bounty campaigns are active right now — check back soon
          </p>
        </div>
      ) : (
        <>
          {referralCampaigns.map(campaign => (
            <ReferralDashboard key={campaign.id} campaign={campaign} />
          ))}
          {claimCampaigns.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {claimCampaigns.map(campaign => (
                <CampaignCard key={campaign.id} campaign={campaign} onClaim={setModalCampaign} />
              ))}
            </div>
          )}
        </>
      )}

      <MyClaimsHistory claims={claims} />
    </div>
  )
}